import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';

// TOKEN_STORAGE_KEY deliberately unused after PINFRA-120 (cookie-based auth)
const THEME_STORAGE_KEY = 'prism_theme';
const API_BASE = process.env.REACT_APP_API_URL || '/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // Token held in memory only — never stored to localStorage (PINFRA-120)
  const [token, setToken] = useState(null);
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  // True while we probe /auth/session on app start (prevents flash to /login)
  const [sessionLoading, setSessionLoading] = useState(true);

  // Theme (non-sensitive, localStorage OK)
  useEffect(() => {
    const savedTheme = localStorage.getItem(THEME_STORAGE_KEY) || 'dark';
    document.documentElement.setAttribute('data-theme', savedTheme);
    // Clean up old JWT entry if present from pre-PINFRA-120 sessions
    localStorage.removeItem('prism_token');
  }, []);

  // Restore session from HttpOnly cookie on app start / page refresh
  useEffect(() => {
    fetch(`${API_BASE}/platform/auth/session`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setToken('cookie'); })
      .catch(() => {})
      .finally(() => setSessionLoading(false));
  }, []);

  const login = useCallback((accessToken) => {
    // Cookie already set by backend — keep JWT in memory for current page session
    setToken(accessToken);
  }, []);

  const logout = useCallback(() => {
    fetch(`${API_BASE}/platform/auth/logout`, { method: 'POST', credentials: 'include' }).catch(() => {});
    setToken(null);
    setProfile(null);
  }, []);

  const userRole = profile?.platform_role || 'user';
  const userType = profile?.user_type || 'external';
  const isAdmin = userRole === 'platform_admin' || userRole === 'org_admin';
  const isInternal = userType === 'internal' || userRole === 'platform_admin';

  const value = useMemo(() => ({
    token,
    profile,
    setProfile,
    profileLoading,
    setProfileLoading,
    sessionLoading,
    userRole,
    userType,
    isAdmin,
    isInternal,
    login,
    logout,
  }), [token, profile, profileLoading, sessionLoading, userRole, userType, isAdmin, isInternal, login, logout]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}

export function applyTheme(theme) {
  const t = theme || 'dark';
  document.documentElement.setAttribute('data-theme', t);
  localStorage.setItem(THEME_STORAGE_KEY, t);
}

export function getStoredTheme() {
  return localStorage.getItem(THEME_STORAGE_KEY) || 'dark';
}
